import React, {Fragment} from "react";
import shopApi from "../../api/shop";
import {IPageProps} from "../../types/pages";
import {GetServerSideProps, NextPage} from "next";
import {domainUrl} from "../../helper";
import Head from "next/head";
import {layoutProps} from "../../services/layoutProps";
import MainLayout from "../../components/ForCWV/Wrappers/MainLayout";
import {StaticPageTranslations} from "../../services/translationKeys";
import SiteCustomPage from "../../components/ForCWV/CustomPages/SiteCustomPage";
import ReCaptchaWrapper from "../../components/shared/ReCaptchaWrapper";
import {useRouter} from "next/router";

const Page: NextPage<IPageProps> = (props) => {
    const {
        dbName,
        domain,
        selectedLocale: locale,
        content: {
            data = [],
            yoast = []
        } = {},
        recaptchaActive,
        recaptchaKey,
        siteName,
        defaultLangCode,
        locales
    } = props;
    const router = useRouter();

    const [{
        meta_title: m_title = data[0].page_title,
        meta_description: m_desc = "",
        meta_keywords: m_keyword = "",
        image: m_img = "",
        url_key: m_url = "",
    } = {}] = data
    const [{
        yoast_facebook_title: FB_title = m_title,
        yoast_facebook_description: FB_desc = m_desc,
        yoast_twitter_title: TW_title = m_title,
        yoast_twitter_description: TW_desc = m_desc,
        yoast_focus_keyword: y_keyword = m_keyword
    } = {}] = yoast
    const canonicalUrl = `${dbName}/${router.locale !== "catchAll" && router.locale !== defaultLangCode ? locale + "/" : ""}page/${m_url}`

    return (
        <ReCaptchaWrapper recaptchaActive={recaptchaActive} recaptchaKey={recaptchaKey} >
            <MainLayout {...props}>
                <Fragment>
                    <Head>
                        <title>{m_title}</title>
                        <meta name="title" content={m_title}/>
                        <meta name="description" content={m_desc}/>
                        <meta name="keywords" content={m_keyword}/>
                        <meta name="generator" content="Zegashop.com"/>
                        <meta name="robots" content="index, follow"/>
                        <meta name="twitter:title" content={TW_title}/>
                        <meta name="twitter:description" content={TW_desc}/>
                        <meta name="twitter:image" content={domainUrl(`${dbName}/storage/${domain}/${m_img}`)}/>
                        <meta property="og:title" content={FB_title}/>
                        <meta property="og:description" content={FB_desc}/>
                        <meta property="og:keywords" content={y_keyword}/>
                        <meta property="og:image" content={domainUrl(`${dbName}/storage/${domain}/${m_img}`)}/>
                        <meta property="og:url" content={domainUrl(canonicalUrl)}/>
                        <meta property="og:locale" content={locale}/>
                        <meta property="og:site_name" content={siteName}/>
                        <link rel="canonical" href={domainUrl(canonicalUrl)}/>
                        {locales?.length > 0 ? (
                            <>
                                <link rel="alternate" href={domainUrl(`${dbName}/page/${m_url}`)} hrefLang="x-default"/>
                                <link rel="alternate" href={domainUrl(`${dbName}/page/${m_url}`)} hrefLang={defaultLangCode}/>
                                {locales.map(intl => (
                                    <link rel="alternate" href={domainUrl(`${dbName}/${intl.code}/page/${m_url}`)} hrefLang={intl.code}/>
                                ))}
                            </>
                        ) : null}
                    </Head>
                    <SiteCustomPage pageSlug={m_url} {...props} />
                </Fragment>
            </MainLayout>
        </ReCaptchaWrapper>
    )
}

export const getServerSideProps: GetServerSideProps = async ({locale, req, query: {pageSlug}}) => {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: StaticPageTranslations,
        ipv4: typedReq.ipv4,
        forEachPageBuilder: true
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, selectedRate, translationsData: {Home: HomeIntl}, isMobile, themeOptions } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: { destination: '/suspend', permanent: false },
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const dbName = req.headers["x-forwarded-host"];

    const pagesOption = {
        locale: selectedLocale,
        pageSlug: pageSlug as string,
        dbName: dbName as string
    }

    const dataContent = await shopApi.getAsyncPagesBySlug(pagesOption, typedReq.m)
    const getBuilderData = await shopApi.getBuilderDataAsync(
        dataContent.data?.[0].cms_page_id, selectedLocale, typedReq.m, req.headers["x-forwarded-host"], typedReq.defaultDB.db_name, selectedRate, isMobile, themeOptions
    ) || []

    if (dataContent?.notFound) {
        return {
            notFound: true
        }
    }
    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: dataContent?.data[0].page_title, url: ``}
    ]

    return {
        props: {
            getBuilderData,
            showBuilderData: Object.values(getBuilderData).some((lll) => (lll as Array<any>)?.length > 0),
            content: dataContent,
            breadcrumb,
            selectedCurrItems: {
                selectedCurrCode: selectedRate.code,
                selectedCurrRate: selectedRate.exchange_rate.rate,
                selectedCurrSymbol: selectedRate.symbol,
            },
            uniqueLayoutId: `page_${dataContent?.data?.[0]?.id}`,
            ...wrapperProps,
        },
    };
};

export default Page